import type { ExtendedProfileT } from "@/types/database/profiles";
import { useSuspenseQuery } from "@tanstack/react-query";

import { useAuth } from "../../../utils/auth";
import { selectProfileByIdQueryOptions } from "../api/selectProfile";

export interface ProfileAttributesT {
    [key: string]: boolean;
}

const noName = (
    profile: {
        title?: string | null;
        first_name?: string | null;
        last_name?: string | null;
    } | null
) => !profile?.title || !profile?.first_name || !profile?.last_name;

const hasOrganisations = (profile: { organisations?: unknown[] | null } | null) =>
    Boolean(profile?.organisations?.length);

/**
 * Profile incomplete attributes — `true` means missing.
 * Presence rules match `buildOnboardingTodos` / profile notification badge
 * (empty string counts as missing, not only SQL null).
 * Onboarding skip flags do not clear these - skipped phone stays incomplete.
 */
export const patientProfile = (profile: ExtendedProfileT): ProfileAttributesT => {
    return {
        organisation: !hasOrganisations(profile),
        name: noName(profile),
        "date-of-birth": !profile?.date_of_birth,
        email: !profile?.email?.trim(),
        phone: !profile?.phone?.trim(),
    };
};

export const clinicianProfile = (profile: ExtendedProfileT): ProfileAttributesT => {
    return {
        organisation: !hasOrganisations(profile),
        name: noName(profile),
        email: !profile?.email,
    };
};

export const secretaryProfile = (profile: ExtendedProfileT): ProfileAttributesT => {
    return {
        organisation: !hasOrganisations(profile),
        name: noName(profile),
        email: !profile?.email,
    };
};

export const managerProfile = (profile: ExtendedProfileT): ProfileAttributesT => {
    return {
        organisation: !hasOrganisations(profile),
        name: noName(profile),
        email: !profile?.email,
    };
};

const useProfileAttributes = () => {
    const auth = useAuth();
    const { data: profile } = useSuspenseQuery(selectProfileByIdQueryOptions(auth.id));

    if (!profile) {
        return {};
    }
    if (auth.roles?.isManagerOnly) {
        return managerProfile(profile);
    }
    if (auth.roles?.isPatient && !!profile) {
        return patientProfile(profile);
    }
    if (auth.roles?.isClinician && !!profile) {
        return clinicianProfile(profile);
    }
    if (auth.roles?.isSecretary && !!profile) {
        return secretaryProfile(profile);
    }
    if (auth.roles?.isAdmin && !!profile) {
        return clinicianProfile(profile);
    }
    if (!auth.roles?.hasRole) {
        return {};
    }
    return {
        organisation: !hasOrganisations(profile),
    };
};

export default useProfileAttributes;
